import { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, Eye } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import {
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Button,
} from '@artstorefronts/ui';
import AdminLayout from '../../../components/AdminLayout';
import { GET_ONBOARDING_VERSIONS, GET_ONBOARDING_FUNNEL, GET_ONBOARDING_STEP_REGISTRY } from '../../../graphql/queries';
import { PreviewModal } from './PreviewModal';
import { GET_ONBOARDING_VERSION, useRegistryByCode, mapVersionStepsToEditorSteps } from './stepMappers';
import { migrateStepConfigOverrides } from './migrateStepOverrides';
import type { RegistryEntry } from './types';

function ConfigValue({ value }: { value: unknown }) {
  // Array of {id, label} option objects → vertical table
  if (
    Array.isArray(value) &&
    value.length > 0 &&
    typeof value[0] === 'object' &&
    value[0] !== null &&
    'label' in value[0]
  ) {
    return (
      <table className="text-xs w-full">
        <thead>
          <tr className="text-left text-gray-400 uppercase tracking-wide">
            <th className="pb-1 pr-4 font-medium">id</th>
            <th className="pb-1 font-medium">label</th>
          </tr>
        </thead>
        <tbody>
          {(value as Array<Record<string, unknown>>).map((opt, i) => (
            <tr key={i} className="border-t border-gray-100">
              <td className="py-1 pr-4 font-mono text-gray-400 whitespace-nowrap">{String(opt.id ?? '—')}</td>
              <td className="py-1 text-gray-800">{String(opt.label ?? '—')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  // Plain array → vertical list
  if (Array.isArray(value)) {
    return (
      <ul className="text-xs space-y-0.5">
        {value.map((item, i) => (
          <li key={i} className="font-mono text-gray-700">
            {String(item)}
          </li>
        ))}
      </ul>
    );
  }

  // Boolean
  if (typeof value === 'boolean') {
    return (
      <span
        className={`px-2 py-0.5 rounded text-xs font-medium ${value ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
      >
        {String(value)}
      </span>
    );
  }

  // Plain object → vertical key/value table
  if (typeof value === 'object' && value !== null) {
    return (
      <table className="text-xs w-full">
        <tbody>
          {Object.entries(value as Record<string, unknown>).map(([k, v]) => (
            <tr key={k} className="border-t border-gray-100">
              <td className="py-1 pr-4 font-mono text-gray-400 whitespace-nowrap">{k}</td>
              <td className="py-1 font-mono text-gray-700">{String(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  // Primitive
  return <span className="font-mono text-gray-900 text-xs">{String(value)}</span>;
}

function OnboardingVersionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewInitialStep, setPreviewInitialStep] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const { data: versionData, loading: versionLoading } = useQuery(GET_ONBOARDING_VERSION, {
    variables: { id },
    skip: !id,
  });

  const { data: allVersionsData } = useQuery(GET_ONBOARDING_VERSIONS);
  const { data: registryData } = useQuery<{ onboardingStepRegistry: RegistryEntry[] }>(GET_ONBOARDING_STEP_REGISTRY);

  const { data: funnelData, loading: funnelLoading } = useQuery(GET_ONBOARDING_FUNNEL, {
    variables: { flowVersionId: id, filters },
    skip: !id,
  });

  const version = versionData?.onboardingVersion;
  const funnel = funnelData?.onboardingFunnel;
  const otherVersions = allVersionsData?.onboardingVersions?.filter((v: any) => v.id !== id);

  const registryByCode = useRegistryByCode(registryData?.onboardingStepRegistry);

  const editorSteps = useMemo(() => mapVersionStepsToEditorSteps(version?.steps, { enabledOnly: true }), [version]);

  const toggleRow = (stepCode: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(stepCode)) {
        next.delete(stepCode);
      } else {
        next.add(stepCode);
      }
      return next;
    });
  };

  const chartData =
    funnel?.steps?.map((step: any) => ({
      stepCode: step.stepCode,
      viewed: step.viewedCount,
      submitted: step.submittedCount,
    })) || [];

  return (
    <AdminLayout
      title={version ? `${version.name} (v${version.version})` : 'Loading...'}
      description="View funnel metrics and cohort analysis"
    >
      {versionLoading ? (
        <div className="text-gray-500">Loading version...</div>
      ) : !version ? (
        <div className="text-red-500">Version not found</div>
      ) : (
        <div className="space-y-6">
          <Link
            to="/admin/onboarding"
            className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to versions
          </Link>

          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              {version.isDefault && (
                <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Default</span>
              )}
            </div>
            <div className="flex gap-2 items-center">
              {editorSteps.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPreviewInitialStep(-1);
                    setPreviewOpen(true);
                  }}
                  className="gap-1.5"
                >
                  <Eye className="w-3.5 h-3.5" />
                  Preview
                </Button>
              )}
              {otherVersions && otherVersions.length > 0 && (
                <Select
                  onValueChange={(value) => {
                    if (value) {
                      navigate(`/admin/onboarding/compare?left=${id}&right=${value}`);
                    }
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Compare to..." />
                  </SelectTrigger>
                  <SelectContent>
                    {otherVersions.map((v: any) => (
                      <SelectItem key={v.id} value={v.id}>
                        v{v.version} - {v.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Funnel Overview</h3>
              {funnelLoading ? (
                <div className="text-gray-500">Loading funnel data...</div>
              ) : funnel ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-500">Total Sessions</div>
                      <div className="text-2xl font-bold">{funnel.totalSessions}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Completion Rate</div>
                      <div className="text-2xl font-bold">{(funnel.completionRate * 100).toFixed(1)}%</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Completed</div>
                      <div className="text-xl font-semibold text-green-600">{funnel.completedSessions}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Abandoned</div>
                      <div className="text-xl font-semibold text-red-600">{funnel.abandonedSessions}</div>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="stepCode" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="viewed" fill="#9ca3af" name="Viewed" />
                      <Bar dataKey="submitted" fill="#16a34a" name="Submitted" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-gray-500">No funnel data available</div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Cohort Filters</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="filter-intent" className="block mb-1">
                    Primary Intent
                  </Label>
                  <Input
                    id="filter-intent"
                    type="text"
                    placeholder="Comma-separated intents"
                    className="w-full"
                    onChange={(e) => {
                      const value = e.target.value;
                      setFilters((prev) => ({
                        ...prev,
                        intents: value ? value.split(',').map((s) => s.trim()) : undefined,
                      }));
                    }}
                  />
                </div>
                <div>
                  <Label htmlFor="filter-career-stage" className="block mb-1">
                    Career Stage
                  </Label>
                  <Input
                    id="filter-career-stage"
                    type="text"
                    placeholder="Comma-separated stages"
                    className="w-full"
                    onChange={(e) => {
                      const value = e.target.value;
                      setFilters((prev) => ({
                        ...prev,
                        careerStages: value ? value.split(',').map((s) => s.trim()) : undefined,
                      }));
                    }}
                  />
                </div>
                <div>
                  <Label htmlFor="filter-started-after" className="block mb-1">
                    Started After
                  </Label>
                  <Input
                    id="filter-started-after"
                    type="date"
                    className="w-full"
                    onChange={(e) => {
                      setFilters((prev) => ({
                        ...prev,
                        startedAfter: e.target.value ? new Date(e.target.value).toISOString() : undefined,
                      }));
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Per-Step Metrics</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8" />
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Step</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Viewed</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Skipped</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Errors</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Drop-off %</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fallback %</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {funnel?.steps?.map((step: any) => {
                    const isExpanded = expandedRows.has(step.stepCode);
                    const reg = registryByCode.get(step.stepCode);
                    const versionStep = version.steps?.find((s: any) => s.stepCode === step.stepCode);
                    const overrides = migrateStepConfigOverrides(
                      step.stepCode,
                      (versionStep?.configOverrides as Record<string, unknown>) ?? {}
                    );
                    const defaults: Record<string, unknown> = reg?.defaultConfig ?? {};
                    const effectiveConfig = { ...defaults, ...overrides };
                    const hasConfig = Object.keys(effectiveConfig).length > 0;
                    const colSpan = 11;
                    // The preview modal only renders enabled steps (editorSteps). A
                    // disabled step has no index there, so it isn't previewable —
                    // don't silently open the modal on the first step instead.
                    const editorIdx = editorSteps.findIndex((s: { stepCode: string }) => s.stepCode === step.stepCode);
                    const canPreview = editorIdx >= 0;

                    return [
                      <tr key={step.stepCode} className={isExpanded ? 'bg-gray-50' : undefined}>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {hasConfig && (
                            <button
                              type="button"
                              onClick={() => toggleRow(step.stepCode)}
                              className="rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                              aria-label={isExpanded ? 'Collapse config' : 'Expand config'}
                              aria-expanded={isExpanded}
                            >
                              {isExpanded ? (
                                <ChevronDown className="w-3.5 h-3.5" />
                              ) : (
                                <ChevronRight className="w-3.5 h-3.5" />
                              )}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {step.stepCode}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{step.position}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{step.viewedCount}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{step.submittedCount}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{step.skippedCount}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{step.errorCount}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {(step.dropOffRate * 100).toFixed(1)}%
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {(step.fallbackRate * 100).toFixed(1)}%
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {step.avgDurationMs ? `${(step.avgDurationMs / 1000).toFixed(1)}s` : 'N/A'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <button
                            type="button"
                            disabled={!canPreview}
                            onClick={() => {
                              if (editorIdx < 0) return;
                              setPreviewInitialStep(editorIdx);
                              setPreviewOpen(true);
                            }}
                            className="rounded p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
                            aria-label={
                              canPreview
                                ? `Preview ${step.stepCode}`
                                : `${step.stepCode} is disabled and cannot be previewed`
                            }
                            title={
                              canPreview ? `Preview ${step.stepCode}` : `${step.stepCode} is disabled in this version`
                            }
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>,
                      isExpanded && hasConfig ? (
                        <tr key={`${step.stepCode}-config`} className="bg-gray-50">
                          <td />
                          <td colSpan={colSpan - 1} className="px-4 pb-4 pt-0">
                            <div className="rounded-md border border-gray-200 bg-white overflow-hidden">
                              <table className="min-w-full text-xs">
                                <thead>
                                  <tr className="border-b border-gray-200 bg-gray-50">
                                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wide w-1/3">
                                      Config key
                                    </th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wide">
                                      Value
                                    </th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wide w-24">
                                      Source
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {Object.entries(effectiveConfig).map(([key, value]) => {
                                    const isOverride = Object.prototype.hasOwnProperty.call(overrides, key);
                                    return (
                                      <tr key={key}>
                                        <td className="px-3 py-2 font-mono text-gray-700">{key}</td>
                                        <td className="px-3 py-2">
                                          <ConfigValue value={value} />
                                        </td>
                                        <td className="px-3 py-2">
                                          {isOverride ? (
                                            <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                              override
                                            </span>
                                          ) : (
                                            <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                                              default
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      ) : null,
                    ];
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {previewOpen && (
        <PreviewModal
          steps={editorSteps}
          registryByCode={registryByCode}
          initialStepIndex={previewInitialStep}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </AdminLayout>
  );
}

export default OnboardingVersionDetailPage;
